import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Shield, Pencil, Check, Eye } from 'lucide-react';
import Breadcrumb from '../../components/Breadcrumb';
import { useAuth } from '../../hooks/useAuth';
import type { Role } from '@shared/types';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  isActive: number;
  lastLoginAt: number | null;
  createdAt: number;
}

export default function AdminUsers() {
  const { user: me, refreshAll } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<{ users: AdminUser[] }>('/admin/users'),
  });

  const qc = useQueryClient();
  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) =>
      api.patch(`/admin/users/${id}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/admin/users/${id}`, { is_active: active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
  const impersonate = useMutation({
    mutationFn: (userId: string) => api.post('/admin/impersonate', { user_id: userId }),
    onSuccess: () => {
      refreshAll();
      // Po invalidaci se /api/me vrátí s impersonator, banner se ukáže,
      // sidebar/sekce se přefiltrují podle cizích oprávnění.
    },
  });

  return (
    <div>
      <Breadcrumb items={[{ label: 'Admin' }, { label: 'Uživatelé' }]} />
      <h1 className="mb-8 font-heading text-3xl font-bold text-dark">Uživatelé</h1>

      {isLoading ? (
        <div className="text-sm text-gray-500">Načítám…</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                <th className="px-4 py-3 text-left">Uživatel</th>
                <th className="px-4 py-3 text-left">E-mail</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Aktivní</th>
                <th className="px-4 py-3 text-left">Poslední přihlášení</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.users.map((u) => {
                const isSelf = me?.id === u.id;
                return (
                <tr key={u.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-semibold">
                    {u.name}
                    {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">ty</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <select
                      value={u.role}
                      disabled={isSelf}
                      title={isSelf ? 'Nemůžeš měnit svou vlastní roli.' : undefined}
                      onChange={(e) =>
                        changeRole.mutate({ id: u.id, role: e.target.value as Role })
                      }
                      className="rounded-md border border-gray-200 px-2 py-1 text-xs disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="reader">Reader</option>
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                    </select>
                    {u.role === 'admin' && <Shield className="ml-2 inline h-3 w-3 text-teal" />}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      disabled={isSelf}
                      title={isSelf ? 'Nemůžeš deaktivovat sám sebe.' : undefined}
                      onClick={() => toggleActive.mutate({ id: u.id, active: u.isActive === 0 })}
                      className={
                        (u.isActive === 1
                          ? 'text-xs font-semibold text-positive'
                          : 'text-xs font-semibold text-gray-400') +
                        ' disabled:cursor-not-allowed disabled:opacity-60'
                      }
                    >
                      {u.isActive === 1 ? (
                        <>
                          <Check className="mr-1 inline h-3 w-3" />
                          Aktivní
                        </>
                      ) : (
                        'Deaktivováno'
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt * 1000).toLocaleString('cs-CZ')
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!isSelf && u.isActive === 1 && (
                      <button
                        onClick={() => impersonate.mutate(u.id)}
                        disabled={impersonate.isPending}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600 transition-colors hover:border-teal hover:text-teal disabled:opacity-60"
                        title="Dočasně zobraz aplikaci jako tento uživatel. Jen čtení, auditováno."
                      >
                        <Eye className="h-3 w-3" />
                        Zobrazit jako
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 flex items-start gap-2 rounded-md bg-teal-light p-4 text-xs text-teal">
        <Pencil className="mt-0.5 h-3 w-3" />
        <div>
          Správa oprávnění na konkrétní sekce/kapitoly: otevři sekci/kapitolu a klikni na panel „Oprávnění". (Fáze 3)
        </div>
      </div>
    </div>
  );
}
